import { useMemo, useRef, useState } from 'react';
import { PlusIcon, TrashIcon, PaperClipIcon, CheckIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { lerValor } from '../CampoParcelas';
import { normalizarItens, AnexosDoItem } from '../CampoItens';
import type { ItemSolicitacao, AnexoItem, ContextoUploadItens } from '../CampoItens';
import { compressImagesIfNeeded } from '../../utils/imageCompression';

/**
 * Campo "Mapa de cotação" (26/10/2026, pedido do André para o Fluxo de compras e pagamentos).
 *
 * Antes a cotação eram três blocos fixos (Cotação 01/02/03) com um valor total cada, sem ligação com
 * os itens que o solicitante lançou na Triagem. Agora o comprador abre quantas cotações quiser: em cada
 * uma informa o fornecedor, prazo e condição, anexa a proposta, marca quais itens da Triagem entram e
 * digita o valor unitário de cada um.
 *
 * Puxado na etapa de aprovação, vira um comparativo (itens nas linhas, fornecedores nas colunas, menor
 * preço destacado). O aprovador escolhe a cotação inteira ou o vencedor item a item; ao aplicar, o
 * fornecedor e o valor vencedores são gravados nos próprios itens (campo "Itens da solicitação"),
 * aprovados — e a divisão do card por fornecedor, que já existe no servidor, faz o resto.
 *
 * Valor gravado: { cotacoes: Cotacao[], escolha: Escolha | null }.
 */

export interface Cotacao {
  id: string;
  fornecedor: string;
  prazoEntrega: string;
  condicao: string;
  observacao: string;
  anexos: AnexoItem[];
  precos: Record<string, number>;   // itemId -> valor unitário (item marcado = tem preço)
  criadoPorNome: string | null;
  criadoEm: string | null;
}
export interface Escolha {
  porItem: Record<string, string>;  // itemId -> cotacaoId vencedora
  decididoPorNome: string | null;
  decididoEm: string | null;
}
export interface MapaCotacao { cotacoes: Cotacao[]; escolha: Escolha | null; }

const novoId = () => `cot_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
const dinheiro = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const MAX_ANEXO_MB = 10;

export const normalizarMapa = (v: any): MapaCotacao => {
  const cot = Array.isArray(v?.cotacoes) ? v.cotacoes : [];
  return {
    cotacoes: cot.filter((c: any) => c && typeof c === 'object').map((c: any) => ({
      id: String(c.id || novoId()), fornecedor: String(c.fornecedor || ''), prazoEntrega: String(c.prazoEntrega || ''),
      condicao: String(c.condicao || ''), observacao: String(c.observacao || ''),
      anexos: Array.isArray(c.anexos) ? c.anexos : [],
      precos: c.precos && typeof c.precos === 'object' ? Object.fromEntries(Object.entries(c.precos).map(([k, x]) => [k, Number(x) || 0])) : {},
      criadoPorNome: c.criadoPorNome || null, criadoEm: c.criadoEm || null,
    })),
    escolha: v?.escolha && typeof v.escolha === 'object' ? {
      porItem: v.escolha.porItem || {}, decididoPorNome: v.escolha.decididoPorNome || null, decididoEm: v.escolha.decididoEm || null } : null,
  };
};
export const ehMapaCotacao = (v: any) => !!v && typeof v === 'object' && !Array.isArray(v) && Array.isArray(v.cotacoes);
const totalCotacao = (c: Cotacao, itens: ItemSolicitacao[]) =>
  itens.reduce((s, i) => (i.id in c.precos ? s + c.precos[i.id] * (i.quantidade ?? 1) : s), 0);
export const textoCotacoes = (v: any, itens?: any): string => {
  const m = normalizarMapa(v); const its = normalizarItens(itens);
  if (!m.cotacoes.length) return '-';
  return m.cotacoes.map((c) => `${c.fornecedor || 'Fornecedor?'}: ${Object.keys(c.precos).length} item(ns)` +
    (its.length ? ` · ${dinheiro(totalCotacao(c, its))}` : '')).join(' | ') + (m.escolha ? ' · escolha feita' : '');
};

interface Props {
  valor: any;
  onChange: (novo: MapaCotacao) => void;
  /** itens da Triagem (valor do campo "Itens da solicitação") */
  itens: any;
  /** editar = comprador (etapa de cotação); aprovar = comparativo com escolha; ver = só leitura */
  modo: 'editar' | 'aprovar' | 'ver';
  /** grava fornecedor/valor vencedores nos itens (modo aprovar) */
  onAplicarItens?: (novos: ItemSolicitacao[]) => void;
  usuario?: { id?: string; nome?: string } | null;
  contextoUpload?: ContextoUploadItens;
}

const CampoCotacoes = ({ valor, onChange, itens: itensBrutos, modo, onAplicarItens, usuario, contextoUpload }: Props) => {
  const mapa = useMemo(() => normalizarMapa(valor), [valor]);
  const itens = useMemo(() => normalizarItens(itensBrutos), [itensBrutos]);
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [porItem, setPorItem] = useState<Record<string, string>>(() => mapa.escolha?.porItem || {});
  const inputArquivo = useRef<HTMLInputElement>(null);
  const alvo = useRef<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const inputCls = 'bg-white text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-gray-500 w-full';

  const salvar = (cotacoes: Cotacao[], escolha = mapa.escolha) => onChange({ cotacoes, escolha });
  const trocar = (id: string, mud: Partial<Cotacao>) => salvar(mapa.cotacoes.map((c) => (c.id === id ? { ...c, ...mud } : c)));
  const abrirNova = () => {
    const c: Cotacao = { id: novoId(), fornecedor: '', prazoEntrega: '', condicao: '', observacao: '', anexos: [], precos: {},
      criadoPorNome: usuario?.nome || null, criadoEm: new Date().toISOString() };
    salvar([...mapa.cotacoes, c]); setAbertaId(c.id);
  };
  const remover = (id: string) => { if (window.confirm('Remover esta cotação?')) salvar(mapa.cotacoes.filter((c) => c.id !== id)); };
  const marcarItem = (c: Cotacao, itemId: string, marcado: boolean) => {
    const precos = { ...c.precos };
    if (marcado) precos[itemId] = precos[itemId] ?? 0; else delete precos[itemId];
    trocar(c.id, { precos });
  };

  const enviar = async (lista: File[]) => {
    const id = alvo.current; if (!id || !lista.length) return;
    if (lista.some((f) => f.size > MAX_ANEXO_MB * 1024 * 1024)) { alert(`⚠️ Arquivo muito grande (máx. ${MAX_ANEXO_MB}MB).`); return; }
    setEnviando(true);
    try {
      const arquivos = await compressImagesIfNeeded(lista);
      const ok: AnexoItem[] = [];
      for (const file of arquivos) {
        try {
          const fd = new FormData();
          fd.append('file', file); fd.append('flowId', contextoUpload?.flowId || 'geral'); fd.append('cardId', contextoUpload?.cardId || `novo_${Date.now()}`);
          fd.append('campoId', contextoUpload?.campoId || 'cotacoes'); fd.append('flowNome', contextoUpload?.flowNome || 'Geral'); fd.append('cardTitulo', contextoUpload?.cardTitulo || 'Novo Card');
          const r = await fetch('/api/upload-anexo-local', { method: 'POST', body: fd });
          const res = await r.json();
          if (!r.ok || !res.url) throw new Error(res.error || `HTTP ${r.status}`);
          ok.push({ name: res.originalName || file.name, url: res.url, type: res.type || file.type, size: res.size ?? file.size,
            fileName: res.fileName || null, uploadedAt: res.uploadedAt || new Date().toISOString(), enviadoPorNome: usuario?.nome || null });
        } catch (e) { console.error('Erro no upload da cotação:', e); alert(`Erro ao enviar ${file.name}`); }
      }
      if (ok.length) { const c = mapa.cotacoes.find((x) => x.id === id); if (c) trocar(id, { anexos: [...c.anexos, ...ok] }); }
    } finally { setEnviando(false); alvo.current = null; if (inputArquivo.current) inputArquivo.current.value = ''; }
  };

  // ---------- comparativo ----------
  const cots = mapa.cotacoes;
  const menor = useMemo(() => {
    const m: Record<string, number> = {};
    itens.forEach((i) => { const ps = cots.filter((c) => i.id in c.precos && c.precos[i.id] > 0).map((c) => c.precos[i.id]); if (ps.length) m[i.id] = Math.min(...ps); });
    return m;
  }, [itens, cots]);
  const escolhaAtual = modo === 'aprovar' ? porItem : (mapa.escolha?.porItem || {});
  const escolherCotacao = (cid: string) => { const c = cots.find((x) => x.id === cid); if (!c) return;
    setPorItem(Object.fromEntries(itens.filter((i) => i.id in c.precos).map((i) => [i.id, cid]))); };
  const escolherMenores = () => setPorItem(Object.fromEntries(itens.flatMap((i) => {
    const c = cots.filter((x) => i.id in x.precos && x.precos[i.id] > 0).sort((a, b) => a.precos[i.id] - b.precos[i.id])[0];
    return c ? [[i.id, c.id]] : []; })));
  const totalEscolha = itens.reduce((s, i) => { const c = cots.find((x) => x.id === escolhaAtual[i.id]); return c ? s + c.precos[i.id] * (i.quantidade ?? 1) : s; }, 0);
  const aplicar = () => {
    if (!Object.keys(porItem).length) { alert('Escolha a cotação vencedora ou os vencedores por item.'); return; }
    const agora = new Date().toISOString();
    const escolha: Escolha = { porItem, decididoPorNome: usuario?.nome || null, decididoEm: agora };
    onChange({ cotacoes: cots, escolha });
    onAplicarItens?.(itens.map((i) => {
      const c = cots.find((x) => x.id === porItem[i.id]);
      return c ? { ...i, fornecedor: c.fornecedor || i.fornecedor, valorUnitario: c.precos[i.id], decisao: 'aprovado' as const,
        decididoEm: agora, decididoPor: usuario?.id || null, decididoPorNome: usuario?.nome || null } : i;
    }));
  };

  const semItens = itens.length === 0;

  const Comparativo = () => (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead><tr className="text-gray-500">
          <th className="text-left font-medium px-2 py-1.5">Item</th><th className="text-right font-medium px-2 py-1.5">Qtd</th>
          {cots.map((c, k) => (
            <th key={c.id} className="px-2 py-1.5 text-right font-semibold text-gray-800 min-w-[120px]">
              {c.fornecedor || `Cotação ${k + 1}`}
              {modo === 'aprovar' && <button type="button" onClick={() => escolherCotacao(c.id)}
                className="block ml-auto mt-1 text-[10px] font-medium text-gray-600 border border-gray-300 rounded px-1.5 py-0.5 hover:bg-gray-900 hover:text-white">escolher esta</button>}
            </th>))}
        </tr></thead>
        <tbody>
          {itens.map((i) => (
            <tr key={i.id} className="border-t border-gray-100">
              <td className="px-2 py-1.5 text-gray-800">{i.item}</td>
              <td className="px-2 py-1.5 text-right text-gray-500">{i.quantidade ?? 1} {i.unidade}</td>
              {cots.map((c) => {
                const tem = i.id in c.precos; const p = c.precos[i.id] || 0; const venc = escolhaAtual[i.id] === c.id; const barato = tem && p > 0 && menor[i.id] === p;
                return (
                  <td key={c.id} onClick={() => modo === 'aprovar' && tem && setPorItem((x) => ({ ...x, [i.id]: c.id }))}
                    className={`px-2 py-1.5 text-right ${modo === 'aprovar' && tem ? 'cursor-pointer hover:bg-gray-100' : ''} ${venc ? 'bg-gray-900 text-white font-semibold rounded' : barato ? 'text-emerald-700 font-semibold' : 'text-gray-700'}`}>
                    {tem ? <>{dinheiro(p)}<span className={`block text-[10px] ${venc ? 'text-gray-300' : 'text-gray-400'}`}>{dinheiro(p * (i.quantidade ?? 1))}</span></> : <span className="text-gray-300">—</span>}
                    {venc && <CheckIcon className="inline w-3 h-3 ml-1" />}
                  </td>);
              })}
            </tr>))}
          <tr className="border-t-2 border-gray-200 font-semibold">
            <td className="px-2 py-1.5" colSpan={2}>Total da cotação</td>
            {cots.map((c) => <td key={c.id} className="px-2 py-1.5 text-right">{dinheiro(totalCotacao(c, itens))}</td>)}
          </tr>
          <tr className="text-gray-500"><td className="px-2 py-1" colSpan={2}>Prazo · condição</td>
            {cots.map((c) => <td key={c.id} className="px-2 py-1 text-right">{[c.prazoEntrega, c.condicao].filter(Boolean).join(' · ') || '—'}</td>)}</tr>
          <tr><td className="px-2 py-1 text-gray-500" colSpan={2}>Proposta</td>
            {cots.map((c) => <td key={c.id} className="px-2 py-1 text-right"><AnexosDoItem anexos={c.anexos} /></td>)}</tr>
        </tbody>
      </table>
    </div>);

  if (modo !== 'editar') {
    if (!cots.length) return <p className="text-xs text-gray-400 p-2">Nenhuma cotação lançada.</p>;
    return (
      <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100 space-y-2">
        <Comparativo />
        {modo === 'aprovar' ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="button" onClick={escolherMenores} className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-100 flex items-center gap-1">
              <TrophyIcon className="w-3.5 h-3.5" />Menor preço por item</button>
            <span className="text-xs text-gray-500">ou clique na cotação / no preço de cada item</span>
            <span className="ml-auto text-xs">Escolhido: <b>{dinheiro(totalEscolha)}</b></span>
            <button type="button" onClick={aplicar} className="text-xs bg-gray-900 text-white rounded px-3 py-1.5 font-semibold">Aplicar escolha aos itens</button>
            {mapa.escolha && <span className="w-full text-[11px] text-gray-500">Última escolha: {mapa.escolha.decididoPorNome || '—'} · {mapa.escolha.decididoEm ? new Date(mapa.escolha.decididoEm).toLocaleString('pt-BR') : ''}</span>}
          </div>
        ) : mapa.escolha ? (
          <p className="text-[11px] text-gray-500">Escolhido por {mapa.escolha.decididoPorNome || '—'} · total {dinheiro(totalEscolha)}</p>
        ) : null}
      </div>);
  }

  // ---------- edição (comprador) ----------
  return (
    <div className="p-2.5 bg-gray-50/80 rounded-lg border border-gray-100 space-y-2">
      <input ref={inputArquivo} type="file" multiple className="hidden" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.eml,.msg"
        onChange={(e) => enviar(Array.from(e.target.files || []))} />
      {semItens && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5">Os itens vêm do campo "Itens da solicitação" da Triagem. Sem itens lá, não há o que cotar por item.</p>}
      {cots.map((c, k) => {
        const aberta = abertaId === c.id || !c.fornecedor;
        return (
          <div key={c.id} className="bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 px-2.5 py-2 cursor-pointer" onClick={() => setAbertaId(aberta && c.fornecedor ? null : c.id)}>
              <span className="text-[10px] font-bold bg-gray-900 text-white rounded px-1.5 py-0.5">{String(k + 1).padStart(2, '0')}</span>
              <b className="text-xs text-gray-900 flex-1">{c.fornecedor || 'Nova cotação'}</b>
              <span className="text-[11px] text-gray-500">{Object.keys(c.precos).length} de {itens.length} itens</span>
              <span className="text-xs font-semibold">{dinheiro(totalCotacao(c, itens))}</span>
              {c.anexos.length > 0 && <PaperClipIcon className="w-3.5 h-3.5 text-gray-500" />}
              <button type="button" onClick={(e) => { e.stopPropagation(); remover(c.id); }} className="text-gray-400 hover:text-red-600"><TrashIcon className="w-3.5 h-3.5" /></button>
            </div>
            {aberta && (
              <div className="border-t border-gray-100 p-2.5 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <label className="text-[11px] text-gray-500">Fornecedor<input className={inputCls} value={c.fornecedor} onChange={(e) => trocar(c.id, { fornecedor: e.target.value })} /></label>
                  <label className="text-[11px] text-gray-500">Prazo de entrega<input className={inputCls} placeholder="ex.: 5 dias úteis" value={c.prazoEntrega} onChange={(e) => trocar(c.id, { prazoEntrega: e.target.value })} /></label>
                  <label className="text-[11px] text-gray-500">Condição de pagamento<input className={inputCls} placeholder="ex.: boleto 28 dias" value={c.condicao} onChange={(e) => trocar(c.id, { condicao: e.target.value })} /></label>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="text-gray-500"><th className="w-6"></th><th className="text-left font-medium py-1">Item</th><th className="text-right font-medium">Qtd</th><th className="text-right font-medium w-28">Valor unitário</th><th className="text-right font-medium w-24">Total</th></tr></thead>
                  <tbody>{itens.map((i) => { const tem = i.id in c.precos; return (
                    <tr key={i.id} className={`border-t border-gray-100 ${tem ? '' : 'text-gray-400'}`}>
                      <td><input type="checkbox" checked={tem} onChange={(e) => marcarItem(c, i.id, e.target.checked)} /></td>
                      <td className="py-1">{i.item}</td><td className="text-right">{i.quantidade ?? 1} {i.unidade}</td>
                      <td className="text-right"><input className={`${inputCls} text-right`} disabled={!tem} inputMode="decimal"
                        defaultValue={tem && c.precos[i.id] ? String(c.precos[i.id]).replace('.', ',') : ''} placeholder={tem ? '0,00' : ''}
                        onBlur={(e) => tem && trocar(c.id, { precos: { ...c.precos, [i.id]: lerValor(e.target.value) || 0 } })} /></td>
                      <td className="text-right font-medium">{tem ? dinheiro((c.precos[i.id] || 0) * (i.quantidade ?? 1)) : ''}</td>
                    </tr>); })}</tbody>
                </table>
                <div className="flex items-center gap-2">
                  <button type="button" disabled={enviando} onClick={() => { alvo.current = c.id; inputArquivo.current?.click(); }}
                    className="text-xs border border-gray-300 rounded px-2 py-1 flex items-center gap-1 hover:bg-gray-100"><PaperClipIcon className="w-3.5 h-3.5" />{enviando ? 'Enviando…' : 'Anexar proposta'}</button>
                  <AnexosDoItem anexos={c.anexos} onRemover={(idx) => trocar(c.id, { anexos: c.anexos.filter((_, j) => j !== idx) })} />
                  <button type="button" onClick={() => trocar(c.id, { precos: Object.fromEntries(itens.map((i) => [i.id, c.precos[i.id] ?? 0])) })}
                    className="ml-auto text-[11px] text-gray-500 hover:text-gray-900">marcar todos os itens</button>
                </div>
              </div>)}
          </div>);
      })}
      <button type="button" onClick={abrirNova} disabled={semItens}
        className="w-full text-xs border border-dashed border-gray-300 rounded-lg py-2 flex items-center justify-center gap-1 text-gray-700 hover:bg-white disabled:opacity-40">
        <PlusIcon className="w-3.5 h-3.5" />Abrir cotação</button>
      {cots.length > 1 && <div className="pt-1"><p className="text-[11px] text-gray-500 mb-1">Comparativo</p><Comparativo /></div>}
    </div>
  );
};

export default CampoCotacoes;
